import { FunctionComponent, SyntheticEvent, useState } from 'react'
import moment from 'moment-timezone'
import styles from './addMultipleRegularSlotsDialog.module.scss'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import { Daycare } from '@/types/Daycare'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import Dialog from '@/components/_custom/Dialog/Dialog'
import { useTranslation } from 'react-i18next'
import DatePickerComponent from '@/components/_custom/Picker/datePicker/datePicker'
import TimePickerComponent from '@/components/_custom/Picker/timePicker/timePicker'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import {
    getDaycareClosing,
    getDaycareDaysSelectOptions,
    getDaycareLastClosing,
    getDaycareLastOpening,
    getDaycareOpening
} from '@/modules/helper/daycareHelper'
import { ChildDaycareWithParents, ChildParent, ChildWithParents } from '@/types/Child'
import { CalendarEventType, EventCalendar } from '@/types/Calendar'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple'
import {
    Autocomplete,
    AutocompleteRenderGetTagProps,
    AutocompleteRenderInputParams,
    Chip,
    TextField
} from '@mui/material'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import SelectMultipleDateDialogComponent from '../../occasionalSlot/occasionalSlotForm/components/SelectMultipleDateDialog/selectMultipleDateDialog.tsx'
import { ClosingDay } from '@/types/ClosingDays'
import { OccasionalSlotsPlaces } from '@/types/Slots.ts'
import OverlapsRegularSlotsDialog from '../overlapsRegularSlotsDialog/overlapsRegularSlotsDialog.tsx'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique.tsx'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { useWeekPattern, UseWeekPatternReturn } from '../weekPatternSection/useWeekPattern'
import WeekPatternSection from '../weekPatternSection/WeekPatternSection'
import { useRegularSlotForm } from '../useRegularSlotForm'
import { useCurrentDaycareInfos } from '@/hooks/useCurrentDaycareInfos.ts'
import { useDialog } from '@/hooks/useDialog/useDialog.tsx'

interface AddMultipleRegularSlotsDialogProps {
    open: boolean
    handleClose: () => void
    currentDaycare: Daycare
    refetchRegularSlots: () => void
    startDateString?: string
    endDateString?: string
    childs: ChildWithParents[]
    calendarEvents: EventCalendar[]
    places: OccasionalSlotsPlaces[]
}

const AddMultipleRegularSlotsDialog: FunctionComponent<AddMultipleRegularSlotsDialogProps> = ({
    open,
    handleClose,
    currentDaycare,
    refetchRegularSlots,
    startDateString,
    endDateString,
    childs,
    calendarEvents,
    places
}: AddMultipleRegularSlotsDialogProps) => {
    const { t } = useTranslation()
    const { showNotificationSuccess, showNotificationError } = useNotification()

    const daycareOpening: string =
        startDateString === endDateString
            ? getDaycareOpening(currentDaycare, (moment(startDateString, 'DD-MM-YYYY').day() + 6) % 7) || '08:00'
            : getDaycareLastOpening(currentDaycare) || '08:00'
    const daycareClosing: string =
        startDateString === endDateString
            ? getDaycareClosing(currentDaycare, (moment(startDateString, 'DD-MM-YYYY').day() + 6) % 7) || '18:00'
            : getDaycareLastClosing(currentDaycare) || '18:00'

    const openHour: number = Number(moment(daycareOpening, 'HH:mm').format('HH'))
    const closeHour: number = Number(moment(daycareClosing, 'HH:mm').format('HH'))
    const openMinute: number = Number(moment(daycareOpening, 'HH:mm').format('mm'))
    const closeMinute: number = Number(moment(daycareClosing, 'HH:mm').format('mm'))

    const { isRpeOrLaep } = useCurrentDaycareInfos()

    const [startDate, setStartDate] = useState<string>(
        startDateString ? startDateString : moment().startOf('month').format('DD-MM-YYYY')
    )
    const [endDate, setEndDate] = useState<string>(
        endDateString ? endDateString : moment().endOf('month').add(1, 'year').format('DD-MM-YYYY')
    )
    const [start, setStart] = useState<string>(daycareOpening)
    const [end, setEnd] = useState<string>(daycareClosing)
    const [selectedChilds, setSelectedChilds] = useState<string[]>([])

    const [chooseDate, setChooseDate] = useState<boolean>(false)
    const [dateList, setDateList] = useState<string[]>([])

    const [chooseDays, setChooseDays] = useState<boolean>(false)
    const [selectedDays, setSelectedDays] = useState<string[]>([])

    const weekPattern: UseWeekPatternReturn = useWeekPattern()

    const [place, setPlace] = useState<string>('')

    const selectDays: SelectOptionsDeprecated = getDaycareDaysSelectOptions(currentDaycare, t)

    const {
        dayTimes,
        daysMode,
        overlapsSlots,
        isOverlapsDialogOpen,
        setOverlapsDialogOpen,
        updateDayTime,
        getDayLabel,
        handleCreate
    } = useRegularSlotForm({
        daycareId: currentDaycare.id,
        childIds: selectedChilds.map((childId: string) => Number(childId)),
        chooseDays,
        weekPattern,
        selectedDays,
        start,
        end,
        startDate,
        endDate,
        chooseDate,
        dateList,
        place,
        selectDays,
        refetchRegularSlots,
        handleClose,
        showNotificationSuccess,
        showNotificationError,
        t
    })

    const selectOptionsPlace: SelectOptionsDeprecated = [
        {
            options: [
                ...places.map(
                    (place: OccasionalSlotsPlaces): SelectOptionDeprecated => ({
                        name: place.name.valueOf(),
                        value: place.id.toString()
                    })
                ),
                { name: t('noPlaces'), value: '' }
            ]
        }
    ]

    const selectOptionsSelectedChilds: SelectOptionsDeprecated = [
        {
            options: childs
                .filter((child: ChildWithParents): ChildDaycareWithParents | undefined =>
                    child.daycares.find((daycare: ChildDaycareWithParents) => {
                        return daycare.daycareId === currentDaycare.id && daycare.visible
                    })
                )
                .filter((child: ChildWithParents): boolean => {
                    return (
                        calendarEvents.filter((event: EventCalendar) => {
                            return (
                                event.childId === child.id &&
                                moment(event.start, 'YYYY-MM-DD HH:mm').format('DD-MM-YYYY') === startDate &&
                                moment(event.start, 'YYYY-MM-DD HH:mm').format('HH:mm') < end &&
                                moment(event.end, 'YYYY-MM-DD HH:mm').format('HH:mm') > start
                            )
                        }).length === 0
                    )
                })
                .map((child: ChildWithParents): SelectOptionDeprecated => {
                    const childDaycare: ChildDaycareWithParents | undefined = child.daycares.find(
                        (daycare: ChildDaycareWithParents): boolean => daycare.daycareId === currentDaycare.id
                    )
                    const complement: string | undefined = childDaycare?.complement
                    const parentNanny: ChildParent | undefined = childDaycare?.parents.find(
                        (parent: ChildParent): boolean => parent.isNanny
                    )
                    return {
                        name:
                            (isRpeOrLaep && parentNanny
                                ? parentNanny.lastname + ' ' + parentNanny.firstname + ' - '
                                : '') +
                            child.firstname +
                            (complement ? ' - ' + complement : ''),
                        value: child.id.toString()
                    }
                })
                .sort((a: SelectOptionDeprecated, b: SelectOptionDeprecated) => a.name.localeCompare(b.name))
        }
    ]

    function handleDateRemove(dateToRemove: string): void {
        setDateList(dateList.filter((date) => date !== dateToRemove))
    }

    function handleCheckChooseDate(checked: boolean): void {
        if (checked) openSelectMultipleDateDialog()
        setChooseDate(checked)
        if (checked) {
            setChooseDays(false)
            weekPattern.setChooseWeekPattern(false)
        }
    }

    function handleCheckChooseDays(checked: boolean): void {
        setChooseDays(checked)
        if (checked) {
            setChooseDate(false)
            weekPattern.setChooseWeekPattern(false)
        }
    }

    function handleCheckChooseWeekPattern(checked: boolean): void {
        weekPattern.setChooseWeekPattern(checked)
        if (checked) {
            setChooseDate(false)
            setChooseDays(false)
        }
    }

    const { dialog: SelectMultipleDateDialog, openDialog: openSelectMultipleDateDialog } = useDialog({
        Component: SelectMultipleDateDialogComponent,
        props: () => ({
            daycare: currentDaycare,
            dateList,
            setDateList,
            startDate,
            closingDays: calendarEvents
                .filter((event: EventCalendar): boolean => event.type === CalendarEventType.CLOSING_DAY)
                .map((event: EventCalendar) => event.slots as ClosingDay)
        }),
        dialogTitle: false
    })

    return (
        <>
            <Dialog open={open} handleClose={handleClose}>
                <DialogHeader title={t('createRegular')} />
                <DialogBody className={styles.DialogBody}>
                    <div className={styles.InputContainer}>
                        {chooseDate && !chooseDays ? (
                            <Autocomplete
                                multiple
                                options={dateList}
                                limitTags={3}
                                value={dateList}
                                onChange={(_event: SyntheticEvent, newValue: string[]) => setDateList(newValue)}
                                renderTags={(value: string[], getTagProps: AutocompleteRenderGetTagProps) =>
                                    value.map((option: string, index: number) => (
                                        <div key={index}>
                                            <Chip
                                                label={option}
                                                {...getTagProps({ index })}
                                                onDelete={() => handleDateRemove(option)}
                                            />
                                        </div>
                                    ))
                                }
                                renderInput={(params: AutocompleteRenderInputParams) => (
                                    <TextField
                                        {...params}
                                        variant='outlined'
                                        rows={1}
                                        multiline={true}
                                        label={t('dateList')}
                                    />
                                )}
                                className={styles.Autocomplete}
                                onOpen={openSelectMultipleDateDialog}
                            />
                        ) : (
                            <>
                                <DatePickerComponent
                                    className={styles.Input}
                                    value={startDate.valueOf()}
                                    setValue={setStartDate}
                                    maxDate={endDate.valueOf()}
                                    disablePast={true}
                                    label={t('fromDate')}
                                />
                                <DatePickerComponent
                                    className={styles.Input}
                                    value={endDate.valueOf()}
                                    setValue={setEndDate}
                                    minDate={startDate.valueOf()}
                                    label={t('toDate')}
                                />
                            </>
                        )}
                    </div>
                    {!chooseDays && !weekPattern.chooseWeekPattern && (
                        <div className={styles.CheckboxContainer}>
                            <Checkbox
                                text={t('chooseDate')}
                                checked={chooseDate}
                                onChange={handleCheckChooseDate}
                                className={styles.Checkbox}
                            />
                        </div>
                    )}
                    {daysMode && selectedDays.length > 0 ? (
                        <div className={styles.DayTimesContainer}>
                            {[...selectedDays]
                                .sort((a, b) => Number(a) - Number(b))
                                .map((day: string) => {
                                    const times: { start: string; end: string } = dayTimes[day] || { start, end }
                                    return (
                                        <div key={day} className={styles.DayTimeRow}>
                                            <span className={styles.DayLabel}>{getDayLabel(day)}</span>
                                            <TimePickerComponent
                                                value={times.start}
                                                setValue={(v: string) => updateDayTime(day, 'start', v)}
                                                minHour={openHour}
                                                minMinute={openMinute}
                                                maxHour={moment(times.end, 'HH:mm').hours()}
                                                maxMinute={moment(times.end, 'HH:mm').minutes()}
                                                label={t('startHour')}
                                                className={styles.DayTimeInput}
                                            />
                                            <TimePickerComponent
                                                value={times.end}
                                                setValue={(v: string) => updateDayTime(day, 'end', v)}
                                                minHour={moment(times.start, 'HH:mm').hours()}
                                                minMinute={moment(times.start, 'HH:mm').minutes()}
                                                maxHour={closeHour}
                                                maxMinute={closeMinute}
                                                label={t('endHour')}
                                                className={styles.DayTimeInput}
                                            />
                                        </div>
                                    )
                                })}
                        </div>
                    ) : (
                        <div className={styles.InputContainer}>
                            <TimePickerComponent
                                value={start.valueOf()}
                                setValue={setStart}
                                minHour={openHour}
                                minMinute={openMinute}
                                maxHour={moment(end.valueOf(), 'HH:mm').hours()}
                                maxMinute={moment(end.valueOf(), 'HH:mm').minutes()}
                                label={t('startHour')}
                                className={styles.Input}
                            />
                            <TimePickerComponent
                                value={end.valueOf()}
                                setValue={setEnd}
                                minHour={moment(start.valueOf(), 'HH:mm').hours()}
                                minMinute={moment(start.valueOf(), 'HH:mm').minutes()}
                                maxHour={closeHour}
                                maxMinute={closeMinute}
                                label={t('endHour')}
                                className={styles.Input}
                            />
                        </div>
                    )}
                    {!chooseDate && (
                        <>
                            <div className={styles.CheckboxContainer}>
                                <Checkbox
                                    text={t('chooseRecurrence')}
                                    checked={chooseDays}
                                    onChange={handleCheckChooseDays}
                                    className={styles.Checkbox}
                                />
                            </div>
                            {chooseDays && (
                                <div className={styles.InputContainer}>
                                    <SelectMultiple
                                        selectOptions={selectDays}
                                        label={t('days')}
                                        selectValues={selectedDays}
                                        setSelectValues={setSelectedDays}
                                        className={styles.Input}
                                    />
                                </div>
                            )}
                            <div className={styles.CheckboxContainer}>
                                <Checkbox
                                    text={t('chooseWeekPattern')}
                                    checked={weekPattern.chooseWeekPattern}
                                    onChange={handleCheckChooseWeekPattern}
                                    className={styles.Checkbox}
                                />
                            </div>
                            {weekPattern.chooseWeekPattern && (
                                <WeekPatternSection
                                    {...weekPattern}
                                    selectedDays={selectedDays}
                                    setSelectedDays={setSelectedDays}
                                    selectDays={selectDays}
                                />
                            )}
                        </>
                    )}
                    <div className={styles.InputContainer}>
                        <SelectMultiple
                            selectOptions={selectOptionsSelectedChilds}
                            selectValues={selectedChilds}
                            setSelectValues={setSelectedChilds}
                            label={t('childs')}
                            className={styles.Input}
                        />
                    </div>
                    {places.length > 0 && (
                        <div className={styles.InputContainer}>
                            <div className={styles.Input}>
                                <SelectUnique
                                    selectOptions={selectOptionsPlace}
                                    selectValue={place}
                                    setSelectValue={setPlace}
                                    formClassName={styles.UserInput}
                                    label={t('places')}
                                />
                            </div>
                        </div>
                    )}
                    <div className={styles.ButtonContainer}>
                        <ButtonTextIcon onClick={handleClose} className={styles.Button} type={'close'} />
                        <ButtonTextIcon
                            onClick={handleCreate}
                            className={styles.Button}
                            type={'confirm'}
                            disabled={
                                selectedChilds.length === 0 ||
                                (chooseDate && dateList.length === 0) ||
                                (chooseDays && selectedDays.length === 0) ||
                                (weekPattern.chooseWeekPattern && selectedDays.length === 0)
                            }
                        />
                    </div>
                </DialogBody>
            </Dialog>
            {SelectMultipleDateDialog}
            {isOverlapsDialogOpen && overlapsSlots && (
                <OverlapsRegularSlotsDialog
                    open={isOverlapsDialogOpen}
                    handleClose={() => setOverlapsDialogOpen(false)}
                    overlapsSlots={overlapsSlots}
                    closeParentDialog={handleClose}
                    regularSlotStart={start}
                    regularSlotEnd={end}
                    refetchData={refetchRegularSlots}
                    daycareId={currentDaycare.id}
                    place={place}
                />
            )}
        </>
    )
}

export default AddMultipleRegularSlotsDialog
